import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusCircle, FolderKanban, Film, Activity, Play, ArrowRight } from 'lucide-react';
import { Header } from '../components/Header.js';
import { QuotaCard } from '../components/QuotaCard.js';
import { EduCard } from '../components/EduCard.js';
import { EduButton } from '../components/EduButton.js';
import { EduBadge } from '../components/EduBadge.js';
import { api } from '../services/api.js';
import { ClientDashboardData } from '../types/index.js';

export const Dashboard: React.FC = () => {
  const [data, setData] = useState<ClientDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    async function load() {
      try {
        const res = await api.getClientDashboard();
        setData(res);
      } catch (err) {
        console.error('Falha ao carregar dashboard do cliente:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  return (
    <div>
      <Header
        title="Dashboard"
        subtitle="Visão geral da sua franquia e produção de videoaulas"
        action={
          <EduButton
            variant="primary"
            size="sm"
            onClick={() => navigate('/nova-videoaula')}
            icon={<PlusCircle size={16} />}
          >
            Nova Videoaula
          </EduButton>
        }
      />

      <div className="p-8 space-y-6 max-w-7xl mx-auto">
        {/* Quota Usage Card */}
        <QuotaCard
          quota={data?.quota || null}
          planName={data?.customer.planName}
          nextRenewal={data?.customer.nextRenewal}
        />

        {/* 3 Metric Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <EduCard
            title={
              <div className="flex items-center gap-2">
                <FolderKanban size={18} className="text-brand-primary" />
                <span>Projetos Cadastrados</span>
              </div>
            }
          >
            <div className="flex items-baseline justify-between">
              <span className="text-3xl font-extrabold text-txt-primary">
                {data?.stats.totalProjects ?? 0}
              </span>
              <button
                onClick={() => navigate('/projetos')}
                className="text-xs text-brand-primary font-medium hover:underline flex items-center gap-1"
              >
                Ver projetos <ArrowRight size={12} />
              </button>
            </div>
            <p className="text-xs text-txt-muted mt-2">
              Estruturas organizacionais de cursos e módulos didáticos.
            </p>
          </EduCard>

          <EduCard
            title={
              <div className="flex items-center gap-2">
                <Film size={18} className="text-status-success" />
                <span>Videoaulas Produzidas</span>
              </div>
            }
          >
            <div className="flex items-baseline justify-between">
              <span className="text-3xl font-extrabold text-txt-primary">
                {data?.stats.totalVideos ?? 0}
              </span>
              <button
                onClick={() => navigate('/biblioteca')}
                className="text-xs text-brand-primary font-medium hover:underline flex items-center gap-1"
              >
                Acessar biblioteca <ArrowRight size={12} />
              </button>
            </div>
            <p className="text-xs text-txt-muted mt-2">
              Total de vídeos MP4 finalizados e disponíveis para download.
            </p>
          </EduCard>

          <EduCard
            title={
              <div className="flex items-center gap-2">
                <Activity size={18} className="text-brand-secondary" />
                <span>Processamentos Ativos</span>
              </div>
            }
          >
            <div className="flex items-baseline justify-between">
              <span className="text-3xl font-extrabold text-brand-secondary">
                {data?.stats.activeJobsCount ?? 0}
              </span>
              <button
                onClick={() => navigate('/processamentos')}
                className="text-xs text-brand-primary font-medium hover:underline flex items-center gap-1"
              >
                Acompanhar fila <ArrowRight size={12} />
              </button>
            </div>
            <p className="text-xs text-txt-muted mt-2">
              Videoaulas em fase de síntese de voz ou renderização de vídeo.
            </p>
          </EduCard>
        </div>

        {/* Recent Generated Videos */}
        <EduCard
          title="Videoaulas Recentes Prontas"
          subtitle="Suas últimas produções concluídas pelo motor de renderização"
          action={
            <EduButton
              variant="secondary"
              size="sm"
              onClick={() => navigate('/biblioteca')}
            >
              Ver Biblioteca Completa
            </EduButton>
          }
        >
          {loading ? (
            <div className="p-8 text-center text-xs text-txt-muted">Carregando vídeos...</div>
          ) : !data?.recentVideos || data.recentVideos.length === 0 ? (
            <div className="text-center py-10">
              <div className="w-12 h-12 rounded-full bg-slate-100 text-txt-muted flex items-center justify-center mx-auto mb-3">
                <Film size={24} />
              </div>
              <h4 className="text-sm font-semibold text-txt-primary">Nenhuma videoaula gerada ainda</h4>
              <p className="text-xs text-txt-secondary mt-1 max-w-sm mx-auto">
                Crie um projeto, envie os materiais no formato Estuda.01 e produza sua primeira aula automaticamente.
              </p>
              <EduButton
                variant="primary"
                size="sm"
                className="mt-4"
                onClick={() => navigate('/nova-videoaula')}
                icon={<PlusCircle size={14} />}
              >
                Produzir Primeira Videoaula
              </EduButton>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.recentVideos.map((video) => (
                <div
                  key={video.id}
                  className="border border-bdr rounded-lg p-3 hover:border-brand-primary transition-all group"
                >
                  <div className="h-28 bg-brand-graphite rounded-md flex items-center justify-center relative overflow-hidden mb-2">
                    <Play className="text-white group-hover:scale-110 transition-transform" size={32} />
                    <span className="absolute bottom-2 right-2 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded font-mono">
                      {Math.floor(video.durationSeconds / 60)}:
                      {(video.durationSeconds % 60).toString().padStart(2, '0')}
                    </span>
                  </div>
                  <h4 className="text-xs font-semibold text-txt-primary truncate">
                    Videoaula {video.lessonId}
                  </h4>
                  <div className="flex items-center justify-between mt-2 text-[11px] text-txt-secondary">
                    <span>{video.resolution}</span>
                    <EduBadge variant="success" size="sm" dot>
                      Pronto
                    </EduBadge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </EduCard>
      </div>
    </div>
  );
};
